import React from "react";
import { CopyButton, Header, Label, Pre, Root } from "./CodeBlock.styles";
import type { CodeBlockProps } from "./CodeBlock.types";

/**
 * A read-only block of code or text in a monospace face, soft-wrapped by default, with an optional
 * caption and copy button. The text is shown exactly as given (no reformatting), and a copy sends
 * that same text: the caller wires `onCopy` (see `useCopyToClipboard`) and the copied state.
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  label,
  ariaLabel,
  wrap = true,
  maxHeight,
  onCopy,
  copyLabel = "Copy",
  copiedLabel = "Copied",
  copied = false,
  disabled = false,
  className,
}) => {
  const name = ariaLabel ?? label ?? undefined;
  return (
    <Root className={className} role="group" aria-label={name} aria-disabled={disabled || undefined} data-testid="code-block">
      {label || onCopy ? (
        <Header>
          {label ? <Label>{label}</Label> : null}
          {onCopy ? (
            <CopyButton type="button" disabled={disabled} onClick={onCopy} aria-label={name ? `${copied ? copiedLabel : copyLabel}: ${name}` : undefined}>
              {copied ? copiedLabel : copyLabel}
            </CopyButton>
          ) : null}
        </Header>
      ) : null}
      <Pre $wrap={wrap} $maxHeight={maxHeight} tabIndex={0} data-testid="code-block-code">
        {code}
      </Pre>
    </Root>
  );
};

export default CodeBlock;
